import { Action, createReducer, on } from "@ngrx/store";

import {
    openCurrentCountryContinentAndCloseOthers,
    setCities,
    setContinents,
    setCountries,
    setCurrentCity,
    setCurrentCountry,
    toggleContinentAndCloseOthers,
} from "./shop-locations.actions";
import {
    CityEntry,
    CountryState,
    initialShopLocationsState,
    SHOP_LOCATIONS_CITY_MODE,
    SHOP_LOCATIONS_NO_SHOPS_MODE,
    ShopLocationsState,
} from "./shop-locations.state";

export const shopLocationsFeatureKey = "shopLocations";

const setContinentsReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof setContinents>,
): ShopLocationsState => ({
    ...state,
    continents: action.continents,
});

const setCountriesReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof setCountries>,
): ShopLocationsState => ({
    ...state,
    countries: action.countries,
});

const setCitiesReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof setCities>,
): ShopLocationsState => ({
    ...state,
    cities: action.cities,
});

const setCurrentCountryReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof setCurrentCountry>,
): ShopLocationsState => {
    const currentCountry = state.countries.find(
        (country) => country.countryCode === action.countryCode,
    );
    if (currentCountry) {
        const updatedContinents = state.continents.map((continent) => {
            const hasCountry = continent.countries.some(
                (country) => country.countryCode === action.countryCode,
            );
            return {
                ...continent,
                closed: hasCountry ? false : continent.closed,
            };
        });

        return {
            ...state,
            pageMode: action.pageMode,
            currentCountry,
            continents: updatedContinents,
        };
    }

    return {
        ...state,
        pageMode: SHOP_LOCATIONS_NO_SHOPS_MODE,
    };
};

const setCurrentCityReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof setCurrentCity>,
): ShopLocationsState => {
    const currentCity = state.cities.find(
        (city: CityEntry) =>
            city.slugifiedCity === action.city.slugifiedCity &&
            city.translatedCity === action.city.translatedCity,
    );
    if (currentCity) {
        return {
            ...(state as CountryState),
            pageMode: SHOP_LOCATIONS_CITY_MODE,
            currentCity,
        };
    }
    return { ...state };
};

const toggleContinentAndCloseOthersReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof toggleContinentAndCloseOthers>,
): ShopLocationsState => {
    const updatedContinents = state.continents.map((continent) => ({
        ...continent,
        closed:
            continent.name === action.continentName ? !continent.closed : true,
    }));
    return {
        ...state,
        continents: updatedContinents,
    };
};

const openCurrentCountryContinentAndCloseOthersReducer = (
    state: ShopLocationsState,
    action: ReturnType<typeof openCurrentCountryContinentAndCloseOthers>,
): ShopLocationsState => {
    const updatedContinents = state.continents.map((continent) => ({
        ...continent,
        closed: continent.name !== action.continentName,
    }));
    return {
        ...state,
        continents: updatedContinents,
    };
};

const shopLocationsReducer = createReducer(
    initialShopLocationsState,
    on(setContinents, setContinentsReducer),
    on(setCountries, setCountriesReducer),
    on(setCurrentCountry, setCurrentCountryReducer),
    on(setCities, setCitiesReducer),
    on(setCurrentCity, setCurrentCityReducer),
    on(toggleContinentAndCloseOthers, toggleContinentAndCloseOthersReducer),
    on(
        openCurrentCountryContinentAndCloseOthers,
        openCurrentCountryContinentAndCloseOthersReducer,
    ),
);

export const reducer = (
    state: ShopLocationsState,
    action: Action,
): ShopLocationsState => shopLocationsReducer(state, action);
